import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import API from "../../api/axiosConfig";
import { Button } from "../button";
import { toast } from "react-toastify";
import { Tag, DollarSign, Percent, X } from "lucide-react";
import { getImageUrl } from "../../utils/getImageUrl";

const getId = (obj) => obj?.id ?? obj?._id;

export default function CreateProductModal({ open, onClose, onCreated, productToEdit }) {
  const [isLoading,   setIsLoading]   = useState(false);
  const [secciones,   setSecciones]   = useState([]);
  const [sedes,       setSedes]       = useState([]);
  const [selectedSedes, setSelectedSedes] = useState([]);

  const [form, setForm] = useState({
    title: "", price: "", description: "", images: [], seccion: "", stock: 0,
  });

  // Discount state
  const [discountActive, setDiscountActive] = useState(false);
  const [discountType,   setDiscountType]   = useState("fixed"); // "fixed" | "percent"
  const [discountValue,  setDiscountValue]  = useState("");

  useEffect(() => {
    if (productToEdit) {
      const op = productToEdit.offerPrice ? parseFloat(productToEdit.offerPrice) : null;
      setForm({
        title:       productToEdit.title       || "",
        price:       productToEdit.price       || 0,
        description: productToEdit.description || "",
        images:      productToEdit.images      || [],
        seccion:     productToEdit.seccionId   ?? productToEdit.seccion?.id ?? productToEdit.seccion?._id ?? "",
        stock:       productToEdit.stock       ?? 0,
      });
      const sedesIds = productToEdit.productSedes?.map((ps) => getId(ps.sede ?? ps))
        ?? productToEdit.sedes?.map((s) => getId(s))
        ?? [];
      setSelectedSedes(sedesIds);
      if (op && parseFloat(productToEdit.price) > 0) {
        setDiscountActive(true);
        setDiscountType("fixed");
        setDiscountValue(String(op));
      } else {
        setDiscountActive(false);
        setDiscountValue("");
      }
    } else {
      setForm({ title: "", price: "", description: "", images: [], seccion: "", stock: 0 });
      setSelectedSedes([]);
      setDiscountActive(false);
      setDiscountValue("");
    }
  }, [productToEdit, open]);

  useEffect(() => {
    if (!open) return;
    const fetchData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const companyId = user?.company?.id ?? user?.company?._id;
        const [secRes, sedeRes] = await Promise.all([
          API.get(`/secciones/company/${companyId}`),
          API.get(`/sedes/company/${companyId}`),
        ]);
        const rawSec  = secRes.data?.data?.secciones ?? secRes.data?.secciones ?? secRes.data?.data?.seccions ?? secRes.data?.seccions ?? secRes.data;
        const rawSede = sedeRes.data?.data?.sedes ?? sedeRes.data?.sedes ?? sedeRes.data;
        setSecciones(Array.isArray(rawSec)  ? rawSec  : []);
        setSedes    (Array.isArray(rawSede) ? rawSede : []);
      } catch {
        toast.error("Error cargando secciones o sedes");
      }
    };
    fetchData();
  }, [open]);

  if (!open) return null;

  const price = parseFloat(form.price) || 0;

  const computedOfferPrice = () => {
    if (!discountActive || !discountValue) return null;
    if (discountType === "percent") {
      const pct = parseFloat(discountValue);
      if (!pct || pct <= 0 || pct >= 100) return null;
      return Math.round(price * (1 - pct / 100));
    }
    const fixed = parseFloat(discountValue);
    if (!fixed || fixed <= 0 || fixed >= price) return null;
    return Math.round(fixed);
  };

  const op  = computedOfferPrice();
  const pct = op && price > 0 ? Math.round(((price - op) / price) * 100) : null;

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) { toast.warn("Máximo 5 imágenes"); return; }
    setForm({ ...form, images: files });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price || selectedSedes.length === 0) {
      toast.warning("Título, precio y al menos una sede son obligatorios");
      return;
    }
    const finalOfferPrice = discountActive ? computedOfferPrice() : null;
    if (discountActive && !finalOfferPrice) {
      toast.warning("El descuento ingresado no es válido");
      return;
    }

    setIsLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const companyId = user?.company?.id ?? user?.company?._id;

      const formData = new FormData();
      formData.append("title",       form.title);
      formData.append("price",       form.price);
      formData.append("description", form.description || "");
      formData.append("companyId",   companyId);
      formData.append("stock",       form.stock ?? 0);
      formData.append("offerPrice",  finalOfferPrice !== null ? String(finalOfferPrice) : "");
      if (form.seccion) formData.append("seccionId", form.seccion);
      selectedSedes.forEach((s) => formData.append("sedes[]", s));
      form.images.forEach((img) => { if (typeof img !== "string") formData.append("images", img); });

      if (productToEdit) {
        await API.put(`/products/${getId(productToEdit)}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Producto actualizado con éxito");
      } else {
        await API.post("/products", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Producto creado con éxito");
      }

      setForm({ title: "", price: 0, description: "", images: [], seccion: "", stock: 0 });
      setSelectedSedes([]);
      setDiscountActive(false);
      setDiscountValue("");
      onCreated();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Error al guardar producto");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center rounded-t-2xl z-10">
          <h2 className="text-base font-bold text-gray-900">
            {productToEdit ? "Editar producto" : "Crear nuevo producto"}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">

          {/* Título */}
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Título *</label>
            <input
              placeholder="Nombre del producto"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-supernova-300 focus:border-supernova-400 outline-none text-sm"
              required
            />
          </div>

          {/* Descripción */}
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Descripción</label>
            <textarea
              placeholder="Describe el producto..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-supernova-300 outline-none text-sm min-h-[80px] resize-none"
            />
          </div>

          {/* Precio + Stock en fila */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Precio *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold">$</span>
                <input
                  type="number"
                  placeholder="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
                  className="w-full pl-7 pr-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-supernova-300 outline-none text-sm"
                  required
                  min="0"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Stock disponible</label>
              <input
                type="number"
                placeholder="0"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })}
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-supernova-300 outline-none text-sm"
                min="0"
              />
            </div>
          </div>

          {/* ── Descuento ─────────────────────────────────────── */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            {/* Toggle header */}
            <button
              type="button"
              onClick={() => { setDiscountActive(!discountActive); setDiscountValue(""); }}
              className={`w-full flex items-center justify-between px-4 py-3 transition-all
                ${discountActive ? "bg-orange-50 border-b border-orange-100" : "bg-gray-50"}`}
            >
              <div className="flex items-center gap-2">
                <Tag size={15} className={discountActive ? "text-orange-500" : "text-gray-400"} />
                <span className={`text-sm font-semibold ${discountActive ? "text-orange-600" : "text-gray-600"}`}>
                  Aplicar descuento
                </span>
              </div>
              <div className={`w-10 h-5 rounded-full transition-all relative ${discountActive ? "bg-orange-500" : "bg-gray-200"}`}>
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${discountActive ? "left-5" : "left-0.5"}`} />
              </div>
            </button>

            {discountActive && (
              <div className="p-4 space-y-3">
                {/* Tipo */}
                <div className="flex gap-2">
                  {[
                    { value: "fixed",   label: "Precio fijo", Icon: DollarSign },
                    { value: "percent", label: "Porcentaje",  Icon: Percent },
                  ].map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => { setDiscountType(value); setDiscountValue(""); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border text-xs font-semibold transition-all
                        ${discountType === value
                          ? "bg-orange-500 text-white border-orange-500"
                          : "bg-white text-gray-600 border-gray-200 hover:border-orange-300"}`}
                    >
                      <Icon size={13} /> {label}
                    </button>
                  ))}
                </div>

                {/* Input */}
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max={discountType === "percent" ? 99 : price - 1}
                    step={discountType === "percent" ? 1 : 100}
                    placeholder={discountType === "percent" ? "Ej: 20 (20%)" : "Precio con descuento"}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="w-full p-3 pr-10 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-300 outline-none text-sm"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">
                    {discountType === "percent" ? "%" : "$"}
                  </span>
                </div>

                {/* Preview */}
                {op && price > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-gray-400">Original</p>
                      <p className="text-xs text-gray-400 line-through">${price.toLocaleString("es-CO")}</p>
                    </div>
                    <span className="bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full">
                      -{pct}% OFF
                    </span>
                    <div className="text-right">
                      <p className="text-[10px] text-gray-400">Con descuento</p>
                      <p className="text-base font-black text-green-600">${op.toLocaleString("es-CO")}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sección */}
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Sección del menú</label>
            <select
              value={form.seccion}
              onChange={(e) => setForm({ ...form, seccion: e.target.value })}
              className="w-full p-3 border border-gray-200 rounded-xl bg-white text-sm focus:ring-2 focus:ring-supernova-300 outline-none"
            >
              <option value="">Sin sección</option>
              {secciones.map((s) => (
                <option key={getId(s)} value={getId(s)}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Sedes */}
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Sedes disponibles *</label>
            <div className="grid grid-cols-2 gap-2 max-h-36 overflow-y-auto border border-gray-200 rounded-xl p-3">
              {sedes.map((sede) => (
                <label key={getId(sede)} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    value={getId(sede)}
                    checked={selectedSedes.includes(getId(sede))}
                    onChange={(e) => {
                      const id = getId(sede);
                      if (e.target.checked) setSelectedSedes([...selectedSedes, id]);
                      else setSelectedSedes(selectedSedes.filter((i) => i !== id));
                    }}
                    className="accent-supernova-500 w-4 h-4"
                  />
                  <span className="text-sm text-gray-700">{sede.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Imágenes */}
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Imágenes (máx. 5)</label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              className="w-full p-2.5 border border-gray-200 rounded-xl text-sm text-gray-500 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-supernova-50 file:text-supernova-700 file:font-semibold file:text-xs"
            />
            {form.images?.length > 0 && (
              <div className="grid grid-cols-5 gap-2 mt-2">
                {form.images.map((img, i) => (
                  <img
                    key={i}
                    src={typeof img === "string" ? getImageUrl(img) : URL.createObjectURL(img)}
                    alt={`preview-${i}`}
                    className="w-full aspect-square rounded-xl object-cover border border-gray-200"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-supernova-500 text-white hover:bg-supernova-600"
            >
              {isLoading ? "Guardando..." : productToEdit ? "Actualizar" : "Crear producto"}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
